package com.shadowings.gero.presentation.screen

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.navigation.NavController
import com.shadowings.gero.domain.model.Characters
import com.shadowings.gero.domain.model.CompendioRuleItem
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.RuleCard
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.presentation.component.BackIconButton
import com.shadowings.gero.presentation.component.MdCWord
import com.shadowings.gero.presentation.component.avatars.InteractiveAvatar
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.viewmodel.RuleDetailViewModel
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun RuleDetailScreen(
    navController: NavController,
    viewModel: RuleDetailViewModel = koinViewModel(),
) {
    val userData by viewModel.userData.collectAsStateWithLifecycle()
    val rule = viewModel.rule

    if (rule != null) {
        RuleDetailScreenContent(
            item = rule,
            userData = userData,
            onBack = { navController.popBackStack() }
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun RuleDetailScreenContent(
    item: CompendioRuleItem,
    userData: UserData,
    onBack: () -> Unit
) {
    Scaffold(
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        text = stringResource(item.title),
                        style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold),
                        color = MaterialTheme.colorScheme.onSurface
                    )
                },
                navigationIcon = {
                    BackIconButton(onBackClick = onBack, testTagId = "rule_detail_btn_back")
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = MaterialTheme.colorScheme.background
                )
            )
        },
        containerColor = MaterialTheme.colorScheme.background
    ) { paddingValues ->
        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues),
            contentPadding = PaddingValues(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            itemsIndexed(item.cards) { index, card ->
                RuleCardView(
                    card = card,
                    showAvatar = index == 0,
                    userData = userData
                )
            }
        }
    }
}

@Composable
private fun RuleCardView(
    card: RuleCard,
    showAvatar: Boolean,
    userData: UserData
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(16.dp),
        color = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.25f),
        border = androidx.compose.foundation.BorderStroke(
            1.dp,
            MaterialTheme.colorScheme.primary.copy(alpha = 0.25f)
        )
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            horizontalArrangement = Arrangement.spacedBy(12.dp),
            verticalAlignment = Alignment.Top
        ) {
            if (showAvatar) {
                InteractiveAvatar(
                    character = Characters.Alyn,
                    userData = userData,
                    baseFace = Faces.Happy,
                    modifier = Modifier.height(56.dp),
                    withBackground = false
                )
            }

            Column(
                modifier = Modifier.weight(1f),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Text(
                    text = stringResource(card.text),
                    modifier = Modifier.fillMaxWidth(),
                    style = MaterialTheme.typography.bodyLarge,
                    color = MaterialTheme.colorScheme.onSurface
                )
                if (card.examples.isNotEmpty()) {
                    Column(
                        verticalArrangement = Arrangement.spacedBy(12.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        card.examples.forEach { example ->
                            Column(
                                verticalArrangement = Arrangement.spacedBy(2.dp),
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                MdCWord(word = example.gardinerCode, rtl = false, cart = false)
                                example.translation?.let { translation ->
                                    Text(
                                        text = stringResource(translation),
                                        style = MaterialTheme.typography.labelMedium,
                                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f)
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@androidx.compose.ui.tooling.preview.Preview
@Composable
private fun RuleDetailScreenPreview() {
    AppTheme {
        RuleDetailScreenContent(
            item = com.shadowings.gero.data.datasource.compendioRules.first(),
            userData = UserData(),
            onBack = {}
        )
    }
}
